/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description constants for claim work lists
 */
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import _ from 'lodash';
import { SORT_COLUMN_LABELS } from '.';

const WORKLIST_TYPES: Record<ClaimWorkLists.IWorkListType, ClaimWorkLists.IWorkListTypeProps> = {
	manual: {
		key: 'manual',
		value: 'manual',
		label: 'Manual',
		newPageTitle: 'Create Manual Worklist',
		editPageTitle: 'Edit Manual Worklist',
		tagColor: ANT_TAG_COLORS.magenta,
	},
	auto: {
		key: 'auto',
		value: 'auto',
		label: 'Automated',
		newPageTitle: 'Create Automated Worklist',
		editPageTitle: 'Edit Automated Worklist',
		tagColor: '#13C2C2',
		tagStyle: {
			border: '1px solid #87E8DE',
		},
	},
};

const WORKLIST_TYPE_TOOLTIP_TEXT: Record<ClaimWorkLists.IWorkListType, string> = {
	manual: 'This is a Manual Worklist',
	auto: 'This is an Automated Worklist',
} as const;

const WORKLIST_PRIORITIES = {
	high: {
		key: 'high',
		label: 'High',
		value: 'high',
		color: '#F5222D',
	},
	medium: {
		key: 'medium',
		label: 'Medium',
		value: 'medium',
		color: '#FA8C16',
	},
	low: {
		key: 'low',
		label: 'Low',
		value: 'low',
		color: '#8C8C8C',
	},
};

const WORKLIST_ENTITY_TYPES: Record<string, ClaimWorkLists.IWorkListEntityType> = {
	claim: 'Claim',
};

const WORKLIST_FORM_FIELDS: Record<keyof ClaimWorkLists.IFormModel, { title: string; placeholder: string; errorMessage: string }> = {
	name: {
		title: 'Name',
		placeholder: 'E.g.: Denied Claims Requiring Immediate Action',
		errorMessage: 'Please enter a valid name',
	},
	description: {
		title: 'Description',
		placeholder: 'E.g.: Claims that have been denied by the payer and require urgent follow-up for resolution',
		errorMessage: 'Please enter a valid Description',
	},
	priority: {
		title: 'Priority',
		placeholder: 'E.g.: High',
		errorMessage: 'Please select a valid Priority',
	},
	assignees: {
		title: 'Assignee(s)',
		placeholder: 'E.g.: John Doe',
		errorMessage: 'Please select a valid Assignee',
	},
	criteria: {
		title: 'Rules',
		placeholder: 'E.g.: Rules',
		errorMessage: 'Please enter a valid Rules',
	},
	tableMeta: {
		title: 'Table Meta',
		placeholder: 'E.g.: Table Meta',
		errorMessage: 'Please enter a valid Table Meta',
	},
	totalCount: {
		title: 'Total Count',
		placeholder: 'E.g.: Total Count',
		errorMessage: 'Please enter a valid Total Count',
	},
};

const WORKLIST_PRIORITY_ORDER = {
	[WORKLIST_PRIORITIES.high.key]: 3,
	[WORKLIST_PRIORITIES.medium.key]: 2,
	[WORKLIST_PRIORITIES.low.key]: 1,
};

const WORKLIST_SEARCH_CRITERIA = {
	name: {
		key: 'name',
		label: 'Name',
		placeholder: 'Search by Worklist Name',
	},
	priority: {
		key: 'priority',
		label: 'Priority',
		placeholder: 'Search by Priority',
	},
	type: {
		key: 'type',
		label: 'Type',
		placeholder: 'Search by Worklist Type',
	},
	assignees: {
		key: 'assignees',
		label: 'Assignee(s)',
		placeholder: 'Search and select Assignee(s)',
	},
};

const WORKLIST_SORT_COLUMNS: Record<string, ClaimWorkLists.IWorklistSortColumn> = {
	priority: 'priority',
	billedAmount: 'claimAmount',
};

// default sort options for the sort table data
const WORKLIST_SORT_COLUMN_DEFAULT_OPTIONS = [
	{
		apikey: WORKLIST_SORT_COLUMNS.priority,
		asc: false,
		label: `Priority (${SORT_COLUMN_LABELS.highToLow})`,
	},
	{
		apikey: WORKLIST_SORT_COLUMNS.billedAmount,
		asc: false,
		label: `Billed (${SORT_COLUMN_LABELS.highToLow})`,
	},
];

// options for the sort table data
const WORKLIST_SORT_COLUMN_OPTIONS: SortItemType[] = _.map(WORKLIST_SORT_COLUMN_DEFAULT_OPTIONS, (option) => ({
	...option,
	key: option.label,
}));

const MANUAL_WORKLIST_ADDITION_LIMIT = 10000;

export {
	WORKLIST_TYPES,
	WORKLIST_PRIORITIES,
	WORKLIST_ENTITY_TYPES,
	WORKLIST_FORM_FIELDS,
	WORKLIST_PRIORITY_ORDER,
	WORKLIST_SEARCH_CRITERIA,
	MANUAL_WORKLIST_ADDITION_LIMIT,
	WORKLIST_SORT_COLUMNS,
	WORKLIST_SORT_COLUMN_OPTIONS,
	WORKLIST_TYPE_TOOLTIP_TEXT,
};
